import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';

import { BetSlipType } from '@bpos';
import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { UserService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';
import { trim } from 'lodash-es';

import { BetStatus, PayoutType, RewardTriggerType } from '../../../../../../../lib/tokens-base/token-base.models';
import { TokensSummaryService } from '../../../../../../../lib/tokens-base/tokens-summary.service';
import { CurrencyPipe } from '../../../../../../base/pipes/currency.pipe';
import { BetAndGetToken } from '../../../../reward-tokens.model';
import { RewardTokensBannerService, TokenCategories } from '../../../../services/reward-tokens-banner.service';
import { getPayoutExpiryInDays } from '../../../../services/reward-tokens.utils';
import { RewardTokenCardBaseComponent } from '../reward-token-card-base.component';
import { RewardTokenLimitation } from '../reward-token-limitations.component';

@Component({
    selector: 'bs-betandget-card',
    templateUrl: './betandget-card.component.html',
    styleUrls: ['../../cards/styles/reward-token-card-shared.scss', './betandget-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetAndGetCardComponent extends RewardTokenCardBaseComponent<BetAndGetToken> implements OnInit {
    @HostBinding('class') className = 'betandget-card';
    @HostBinding('class.reward-drawer__details') get formCoupon(): boolean {
        return this.isRewardDrawerEnabled;
    }

    @Input() override isRewardDrawerEnabled: boolean;
    tokenLimitations: RewardTokenLimitation[];
    tokenCategories: TokenCategories;
    linkTitle = '';
    expireTokenReward: RewardTokenLimitation[];
    betType: RewardTokenLimitation[];
    promoHubUrlTokenType: string;
    tokenPayoutTitle: string;

    constructor(
        public override sitecore: Sitecore,
        protected override crmConfig: CrmConfig,
        protected override intlService: IntlService,
        protected override user: UserService,
        protected override tokensPayoutInfo: TokensSummaryService,
        private currencyPipe: CurrencyPipe,
        protected override oddsFormatterService: OddsFormatterService,
        private rewardTokensBannerService: RewardTokensBannerService,
        private interpolationPipe: StringInterpolationPipe,
    ) {
        super(sitecore, crmConfig, user, intlService, oddsFormatterService, tokensPayoutInfo);
    }

    ngOnInit(): void {
        this.tokenLimitations = this.getTokenLimitations();
        this.tokenCategories = this.rewardTokensBannerService.getTokenCategories(this.token);
        this.expireTokenReward = this.getTokenUserExpire();
        this.betType = this.getTokenBetType();
        this.promoHubUrlTokenType = this.sitecore.crmTokens.ctaLinks.promoCtaLinks.BetAndGet;
        this.tokenPayoutTitle = this.isRewardDrawerEnabled ? `${this.sitecore.crmTokens.cards.TokenPayout}:` : this.payoutTitle;
    }

    get payoutTitle(): string {
        switch (this.token.rewardTriggerType) {
            case RewardTriggerType.BetPlacement:
                return this.sitecore.crmTokens.betAndGet.card.BetPlacementTemplate;
            case RewardTriggerType.BetSettlement:
                switch (this.token.betStatus) {
                    case BetStatus.Any:
                        return this.sitecore.crmTokens.betAndGet.card.BetSettlementTemplateForAny;
                    case BetStatus.Win:
                        return this.sitecore.crmTokens.betAndGet.card.BetSettlementTemplateForWin;
                    case BetStatus.Loss:
                        return this.sitecore.crmTokens.betAndGet.card.BetSettlementTemplateForLoss;
                    default:
                        return '';
                }
            default:
                return '';
        }
    }

    get payoutDetails(): string {
        let displayTemplate = '';
        let rewardValue: string | number = '';
        switch (this.token.payoutType) {
            case PayoutType.FreeBet:
                displayTemplate = this.token.percentagePayout
                    ? this.sitecore.crmTokens.betAndGet.card.FreebetPercentagePayoutTemplate
                    : this.sitecore.crmTokens.betAndGet.card.FreebetPayoutTemplate;
                break;
            case PayoutType.Bonus:
                displayTemplate = this.token.percentagePayout
                    ? this.sitecore.crmTokens.betAndGet.card.BonusPercentagePayoutTemplate
                    : this.sitecore.crmTokens.betAndGet.card.BonusPayoutTemplate;
                break;
            case PayoutType.Cash:
                displayTemplate = this.token.percentagePayout
                    ? this.sitecore.crmTokens.betAndGet.card.CashPercentagePayoutTemplate
                    : this.sitecore.crmTokens.betAndGet.card.CashPayoutTemplate;
                break;
            case PayoutType.OddsBoostToken:
                return this.sitecore.crmTokens.betAndGet.card.OddsBoostPayoutTemplate;
            case PayoutType.RiskFreeToken:
                return this.sitecore.crmTokens.betAndGet.card.RiskFreePayoutTemplate;
        }
        if (this.token.percentagePayout) {
            return this.maxReturnPercentagePayout(displayTemplate);
        }
        rewardValue = this.currencyPipe.transform(this.token.payoutValue, true, 2, true, undefined, undefined, false);

        return trim(
            interpolateDoubleBracesString(displayTemplate, {
                value: rewardValue,
            }),
        );
    }

    private maxReturnPercentagePayout(percentagePayoutTemplate: string): string {
        return this.interpolationPipe.transform(percentagePayoutTemplate, {
            value: this.token.percentagePayout,
            maxAmount: this.currencyPipe.transform(this.token.maximumPayout, true, 2, true, undefined, undefined, false),
        });
    }

    override getTokenLimitations() {
        const result: RewardTokenLimitation[] = super.getTokenLimitations();
        let slipType: string;
        const minLegs = result.find((x) => x.name === this.sitecore.crmTokens.cards.TokenMinLegs);
        const index = result.findIndex((item) => {
            return item.name === this.sitecore.crmTokens.cards.BetType;
        });
        if (index >= 0) {
            switch (this.token.filter.betSlipType) {
                case BetSlipType.Single:
                    slipType = this.sitecore.crmTokens.betAndGet.card.BetTypeSingle;
                    break;
                case BetSlipType.Combo:
                    slipType = Number(minLegs?.value ?? 0) > 1 ? '' : this.sitecore.crmTokens.betAndGet.card.BetTypeMultiple;
                    break;
                default:
                    slipType = '';
            }
            if (slipType) {
                result[index].value = slipType!;
            } else {
                result.splice(index, 1);
            }
        }

        if (this.token.minimumStake) {
            result.push({
                name: this.sitecore.crmTokens.betAndGet.card.MinStake,
                value: this.currencyPipe.transform(this.token.minimumStake, true, 2, true, undefined, undefined, false),
            });
        }

        return result;
    }

    get expiryDetails() {
        if (this.token.payoutExpiry) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.betAndGet.card.PayoutExpiryTemplate, {
                count: getPayoutExpiryInDays(this.token),
            });
        }

        return '';
    }
}
